import { useCallback, useEffect, useState } from "react";

import {
  type DashboardAdminAgentModeBudgetDefault,
  type DashboardAdminAgentModeBudgetsReseedResponse,
  listDefaultAgentModeBudgetsAsAdmin,
  reseedAgentModeBudgetsForOrganizationsAsAdmin,
} from "../dashboard-client";

interface UseAdminAgentModeBudgetsState {
  defaults: DashboardAdminAgentModeBudgetDefault[];
  error: string | null;
  lastResult: DashboardAdminAgentModeBudgetsReseedResponse | null;
  loading: boolean;
}

interface ReseedBudgetsInput {
  mode?: string;
  organizationIds?: string[];
}

export function useAdminAgentModeBudgets() {
  const [state, setState] = useState<UseAdminAgentModeBudgetsState>({
    defaults: [],
    error: null,
    lastResult: null,
    loading: false,
  });

  const refresh = useCallback(async () => {
    setState((current) => ({ ...current, error: null, loading: true }));
    try {
      const { data } = await listDefaultAgentModeBudgetsAsAdmin({});
      setState((current) => ({
        ...current,
        defaults: data.defaults,
        error: null,
        loading: false,
      }));
    } catch (error) {
      setState((current) => ({
        ...current,
        error:
          error instanceof Error
            ? error.message
            : "Failed to load default agent mode budgets.",
        loading: false,
      }));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const reseed = useCallback(
    async (
      input: ReseedBudgetsInput,
    ): Promise<DashboardAdminAgentModeBudgetsReseedResponse> => {
      const { data } = await reseedAgentModeBudgetsForOrganizationsAsAdmin({
        body: {
          mode: input.mode,
          organization_ids:
            input.organizationIds && input.organizationIds.length > 0
              ? input.organizationIds
              : undefined,
        },
      });
      setState((current) => ({ ...current, error: null, lastResult: data }));
      return data;
    },
    [],
  );

  return {
    defaults: state.defaults,
    error: state.error,
    lastResult: state.lastResult,
    loading: state.loading,
    refresh,
    reseed,
  };
}
